import { gql, useApolloClient } from '@apollo/client'
import openAttachment from 'components/Dashboard/openAttachment'
import RawJsonDisplay from 'components/Dashboard/RawJsonDisplay'
import ViewPhotoModal from 'components/Dashboard/ViewPhotoModal'
import { formatDate } from 'helpers/string'
import { orderBy } from 'lodash'
import { useState } from 'react'
import LabEventEvent, { labEventEventFragment } from './LabEventEvent'
import SmsSentEvent, { smsSentEventFragment } from './SmsSentEvent'
import { KitHistoryFragment } from './__generated__/KitHistoryFragment'

export const kitHistoryFragment = gql`
  fragment KitHistoryFragment on Kit {
    history {
      createdAt
      __typename

      ... on OtherEvent {
        SK
      }

      ... on OutboundEmailEvent {
        outboundEmail {
          id
          status
          submittedAt
          recipients {
            type
            email
            status
          }
          template
          templateParamsJson
          attachments {
            name
          }
        }
      }

      ... on PendingResultReceivedEvent {
        filename
        labData {
          labNo
        }
      }

      ... on ResultReceivedEvent {
        filename
        labData {
          labNo
          resultDate
          result
        }
      }

      ... on MultiResultReceivedEvent {
        result
      }

      ... on NewVersionEvent {
        reason {
          adminUser {
            email
          }
          reason
          processOrContext
          regCorrections
        }
        version
      }

      ... on AdminNoteEvent {
        note
      }

      ... on LabEventEvent {
        ...LabEventEventFragment
      }

      ... on KitOrderedEvent {
        order {
          id
          createdAt
          paymentStatus
        }
      }

      ... on KitRegisteredEvent {
        firstName
        lastName
        email
        dob
      }

      ... on SmsReceivedEvent {
        messageText
      }

      ... on SmsSentEvent {
        ...SmsSentEventFragment
      }

      ... on ViewCertificateEvent {
        createdAt
      }
      ... on DownloadCertificateEvent {
        createdAt
      }
    }
  }

  ${labEventEventFragment}
  ${smsSentEventFragment}
`

const KitOrderedEvent = ({ event }) => (
  <div>
    Associated with order: {event.createdAt} {event.order.paymentStatus}
  </div>
)

const OtherEvent = ({ event }) => (
  <div>
    Other event {event.__typename} {event.SK}
  </div>
)

const KitRegisteredEvent = ({ event }) => (
  <div>
    <strong>Reg details:</strong> {event.firstName} {event.lastName}, {event.dob}, {event.email}
  </div>
)

const MultiResultReceivedEvent = ({ event }) => (
  <div>
    <strong>Result:</strong> {event.result}
  </div>
)

const AdminNoteEvent = ({ event }) => <div>"{event.note}"</div>

const SmsReceivedEvent = ({ event }) => (
  <div>
    <p>
      <strong>Message: </strong>
      {event.messageText}
    </p>
  </div>
)

const PendingResultReceivedEvent = ({ event }) => (
  <div>
    Filename: {event.filename}
    <br />
    Lab no: {event.labData?.labNo}
  </div>
)

const ResultReceivedEvent = ({ event }) => (
  <div>
    Filename: {event.filename}
    <br />
    <RawJsonDisplay json={event.labData} />
  </div>
)

const ViewCertificateEvent = ({ event }) => <div>Certificate Viewed by Customer</div>

const DownloadCertificateEvent = ({ event }) => <div>Certificate Downloaded by Customer</div>

const NewVersionEvent = ({ event }) => {
  const [showModal, setShowModal] = useState(false)

  const photoKey = JSON.parse(event.version).PhotoKey

  return (
    <div>
      {event.reason.reason}
      {event.reason.adminUser && <> - ({event.reason.adminUser.email})</>}
      <br />
      {photoKey && (
        <>
          <button className="button is-small" onClick={() => setShowModal(true)}>
            View Photo
          </button>
          <br />
        </>
      )}
      <RawJsonDisplay json={event.version} />
      {showModal && <ViewPhotoModal photoKey={photoKey} onClose={() => setShowModal(false)} />}
    </div>
  )
}

const OutboundEmailEvent = ({ event }) => {
  const client = useApolloClient()

  const email = event.outboundEmail

  return (
    <div>
      <p>
        <strong>Template: </strong>
        {email.template}
      </p>
      <p>
        {email.recipients.map((r) => (
          <>
            <strong>{r.type.toUpperCase()}: </strong>
            {r.email} (<span className="is-family-monospace">{r.status}</span>)
            <br />
          </>
        ))}
      </p>

      <RawJsonDisplay json={email.templateParamsJson} />

      {email.attachments.map((attach) => (
        <p key={attach.name} className="is-flex is-align-items-center">
          Attachment: {attach.name}
          <button
            className="button is-small is-text"
            onClick={() => {
              openAttachment(client, email.id, attach)
            }}
          >
            Open
          </button>
        </p>
      ))}
    </div>
  )
}

const eventsToComponents = {
  KitOrderedEvent,
  KitRegisteredEvent,
  AdminNoteEvent,
  OutboundEmailEvent,
  PendingResultReceivedEvent,
  ResultReceivedEvent,
  NewVersionEvent,
  SmsReceivedEvent,
  SmsSentEvent,
  MultiResultReceivedEvent,
  LabEventEvent,
  ViewCertificateEvent,
  DownloadCertificateEvent,
}

const eventsToTitles = {
  KitOrderedEvent: 'Kit associated with order',
  KitRegisteredEvent: 'Registration form completed',
  AdminNoteEvent: 'Admin note',
  OutboundEmailEvent: 'Email sent',
  PendingResultReceivedEvent: 'Pending result received',
  ResultReceivedEvent: 'Result received',
  NewVersionEvent: 'Kit data updated',
  SmsReceivedEvent: 'SMS Received',
  SmsSentEvent: 'Outbound SMS',
  MultiResultReceivedEvent: 'Self-reported result received for multi-result ID',
  LabEventEvent: 'Lab data received',
  ViewCertificateEvent: 'Certificate Viewed',
  DownloadCertificateEvent: 'Certificate Downloaded',
}

const KitHistory = ({ kit }: { kit: KitHistoryFragment }) => (
  <>
    {orderBy(kit.history, 'createdAt', 'desc').map((event) => {
      const Component = eventsToComponents[event.__typename] || OtherEvent

      return (
        <article key={`${event.createdAt}-${event.__typename}`} className="message">
          <div className="message-header">
            <p className="has-text-weight-light">
              {formatDate(event.createdAt)} &mdash; {eventsToTitles[event.__typename] || 'other'}
            </p>
          </div>
          <div className="message-body">
            <Component event={event} />
          </div>
        </article>
      )
    })}
  </>
)

export default KitHistory
